// ============================================
// SERVICIO DE AUTENTICACIÓN - FASE 15 PRODUCCIÓN
// ============================================
// CAMBIOS:
// - FIX: No redirigir en 401 si es endpoint de login/register
// - Mejor manejo de errores de red
// - Timeout configurado

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Configurar axios con timeout y credenciales
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 15000 // 15 segundos timeout
});

// ============================================
// INTERCEPTOR DE REQUEST
// ============================================
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================
// INTERCEPTOR DE RESPONSE - FIX BUG RECARGA + RETRY 429
// ============================================
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = error.config?.url || '';
    const status = error.response?.status;

    // ============================================
    // FIX F14: RETRY automático en 429 (rate limit)
    // ============================================
    if (status === 429 && !error.config._retryCount) {
      error.config._retryCount = (error.config._retryCount || 0) + 1;

      // Máximo 2 reintentos
      if (error.config._retryCount <= 2) {
        // Esperar 2 segundos antes de reintentar
        const delay = error.config._retryCount * 2000;
        console.warn(`⏳ Rate limit alcanzado, reintentando en ${delay / 1000}s... (intento ${error.config._retryCount})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return api(error.config);
      }

      // Si agotó reintentos, NO cerrar sesión — solo fallar la petición
      console.warn('⚠️ Rate limit: reintentos agotados, petición descartada');
      return Promise.reject(error);
    }

    // ============================================
    // FIX: NO redirigir si es endpoint de auth
    // ============================================
    const isAuthEndpoint = 
      requestUrl.includes('/auth/login') || 
      requestUrl.includes('/auth/register');
    
    // Solo redirigir si es 401 (token inválido), NUNCA en 429
    if (status === 401 && !isAuthEndpoint) {
      const hasToken = localStorage.getItem('token');
      if (hasToken) {
        console.warn('Token expirado o inválido, redirigiendo al login...');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// ============================================
// SERVICIO DE AUTENTICACIÓN
// ============================================
const authService = {
  // Registrar nuevo usuario
  register: async (nombre, email, password, rol = 'VENDEDOR') => {
    const response = await api.post('/auth/register', {
      nombre,
      email,
      password,
      rol
    });
    return response.data.data;
  },

  // Iniciar sesión
  login: async (email, password) => {
    const response = await api.post('/auth/login', {
      email,
      password
    });
    return response.data.data;
  },

  // Cerrar sesión
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
    localStorage.removeItem('token');
  },

  // Obtener perfil del usuario actual
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data.data;
  },

  // Obtener planes disponibles
  getPlans: async () => {
    const response = await api.get('/auth/plans');
    return response.data.data;
  },

  // Cambiar plan
  changePlan: async (planId) => {
    const response = await api.post('/auth/change-plan', { planId });
    return response.data.data;
  }
};

export default authService;
